'use client'

import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useGetCategories } from '@/actions/category'
import { useGetFormations } from '@/actions/formation'

interface DropdownOption {
    value: string
    label: string
}

interface DropdownProps {
    label: string
    options: DropdownOption[]
    value: string
    onChange: (value: string) => void
    openDropdown: string | null
    setOpenDropdown: (name: string | null) => void
    dropdownName: string
}

const Dropdown: React.FC<DropdownProps> = ({ label, options, value, onChange, openDropdown, setOpenDropdown, dropdownName }) => {
    const ref = useRef<HTMLDivElement>(null)
    const isOpen = openDropdown === dropdownName

    useEffect(() => {
        if (!isOpen) return
        const handleClickOutside = (event: MouseEvent) => {
            if (ref.current && !ref.current.contains(event.target as Node)) {
                setOpenDropdown(null)
            }
        }
        document.addEventListener('click', handleClickOutside)
        return () => document.removeEventListener('click', handleClickOutside)
    }, [isOpen, setOpenDropdown])

    const selectedLabel = options.find(opt => opt.value === value)?.label || options[0].label

    return (
        <div className="flex flex-col" ref={ref}>
            <label className="text-[#1B365E] text-[14px] leading-[20px] font-semibold mb-1.5">{label}</label>
            <div className="relative">
                <button
                    onClick={() => setOpenDropdown(isOpen ? null : dropdownName)}
                    onMouseDown={(e) => e.stopPropagation()}
                    className="w-full bg-[#F0F0F0] rounded-full px-4 py-3 text-[14px] leading-[20px] text-[#333] text-left flex items-center justify-between outline-none focus:ring-1 cursor-pointer focus:ring-[#1B365E]/20 transition-all"
                >
                    <span className="truncate">{selectedLabel}</span>
                    <svg
                        className={`ml-2 w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                </button>
                {isOpen && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl shadow-lg border border-[#EFEFEF] max-h-[250px] no-scrollbar overflow-y-auto z-50">
                        {options.map((option) => (
                            <button
                                key={option.value}
                                onClick={(e) => {
                                    e.stopPropagation()
                                    onChange(option.value)
                                    setOpenDropdown(null)
                                }}
                                onMouseDown={(e) => e.stopPropagation()}
                                className={`w-full px-4 py-2 text-left text-[14px] hover:bg-[#F5F5F5] transition-colors cursor-pointer  ${value === option.value ? 'bg-[#1B365E] text-white hover:text-black hover:bg-[#1B365E]' : 'text-[#333]'}`}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    )
}

const ALL_THEMATIQUES_OPTION: DropdownOption = { value: 'all', label: 'Toutes les thématiques' }
const ALL_VILLES_OPTION: DropdownOption = { value: 'all', label: 'Toutes les villes' }

export interface SearchBarFilters {
    keywords: string
    category: string
    ville: string
    presentiel: boolean
    distance: boolean
}

const DEFAULT_FILTERS: SearchBarFilters = {
    keywords: '',
    category: 'all',
    ville: 'all',
    presentiel: false,
    distance: false,
}

interface SearchBarProps {
    initialFilters?: Partial<SearchBarFilters>
    /** Fired with the full filter snapshot on every change (dropdown, checkbox, keyword, search, reset). */
    onFilterChange?: (filters: SearchBarFilters) => void
    /** Fired with the full filter snapshot only when the search button is clicked.
     *  When provided, it takes precedence over `onFilterChange` for that click —
     *  used on the home page to navigate to the catalog instead of filtering live. */
    onSearch?: (filters: SearchBarFilters) => void
}

const SearchBar: React.FC<SearchBarProps> = ({ initialFilters, onFilterChange, onSearch }) => {
    const [filters, setFilters] = useState<SearchBarFilters>({ ...DEFAULT_FILTERS, ...initialFilters })
    const [openDropdown, setOpenDropdown] = useState<string | null>(null)

    // Re-seed local state when the parent's filters change externally (e.g. category picked from the sidebar)
    useEffect(() => {
        if (!initialFilters) return
        setFilters((prev) => ({ ...prev, ...initialFilters }))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialFilters?.category, initialFilters?.ville, initialFilters?.presentiel, initialFilters?.distance])

    const { categories } = useGetCategories()
    const { formations } = useGetFormations()

    const thematiqueOptions = useMemo<DropdownOption[]>(() => {
        const options: DropdownOption[] = [ALL_THEMATIQUES_OPTION]
        for (const category of categories) {
            options.push({ value: category.slug, label: category.name })
            for (const sub of category.subcategories ?? []) {
                options.push({ value: sub.slug, label: sub.name })
            }
        }
        return options
    }, [categories])

    const villeOptions = useMemo<DropdownOption[]>(() => {
        const villes = new Set<string>()
        for (const formation of formations) {
            // Only on-site formations have a real city in `location` — remote ones fall back
            // to a video-conference/e-learning URL there, which isn't a "Ville". Also guard
            // against 'N/A' (our own missing-data sentinel, not a real location).
            if (formation.modality?.includes('presentiel') && formation.location && formation.location !== 'N/A') {
                villes.add(formation.location)
            }
        }
        return [
            ALL_VILLES_OPTION,
            ...[...villes].sort((a, b) => a.localeCompare(b)).map((ville) => ({ value: ville, label: ville })),
        ]
    }, [formations])

    const emit = (next: Partial<SearchBarFilters>) => {
        const updated = { ...filters, ...next }
        setFilters(updated)
        onFilterChange?.(updated)
    }

    const handleReset = () => {
        setFilters(DEFAULT_FILTERS)
        onFilterChange?.(DEFAULT_FILTERS)
    }

    return (
        <div className=''>
            <div className="w-full max-w-[1240px] mx-auto">
                {/* Main Search Container */}
                <div className="bg-white border border-black/10 rounded-lg p-4 md:p-6 relative">
                    {/* Top Row - Search Fields */}
                    <div className="flex flex-col lg:flex-row gap-3 sm:gap-4 mb-4">
                        {/* Keywords - Wider to fill space */}
                        <div className="flex flex-col lg:flex-[1.2] lg:w-[300px]">
                            <label className="text-[#1B365E] text-[14px] leading-[20px] font-semibold mb-1.5">
                                Recherche par mots-clés
                            </label>
                            <input
                                type="text"
                                placeholder="Mots-clés"
                                value={filters.keywords}
                                onChange={(e) => emit({ keywords: e.target.value })}
                                className="bg-[#F0F0F0] rounded-full px-4 py-3 text-[14px] text-[#333] placeholder-[#999] outline-none focus:ring-1 focus:ring-[#1B365E]/20 transition-all w-full"
                            />
                        </div>

                        {/* Thematiques Dropdown */}
                        <div className="flex flex-col lg:flex-1 w-full lg:w-[300px]">
                            <Dropdown
                                label="Thématiques"
                                options={thematiqueOptions}
                                value={filters.category}
                                onChange={(value) => emit({ category: value })}
                                openDropdown={openDropdown}
                                setOpenDropdown={setOpenDropdown}
                                dropdownName="thematiques"
                            />
                        </div>

                        {/* Ville Dropdown */}
                        <div className="flex flex-col lg:flex-1">
                            <Dropdown
                                label="Ville"
                                options={villeOptions}
                                value={filters.ville}
                                onChange={(value) => emit({ ville: value })}
                                openDropdown={openDropdown}
                                setOpenDropdown={setOpenDropdown}
                                dropdownName="villes"
                            />
                        </div>

                        <div className='flex items-end justify-end'>
                            <button
                                onClick={() => (onSearch ?? onFilterChange)?.(filters)}
                                className="text-white bg-gradient-to-b from-[#FFBC41] to-[#FF5758] text-[18px] cursor-pointer rounded-full w-full sm:w-[44px] h-[44px] transition-colors flex items-center justify-center gap-3 sm:gap-0 shrink-0"
                            >
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                    <circle cx="11" cy="11" r="8" />
                                    <path d="m21 21-4.35-4.35" />
                                </svg>
                                <span className='sm:hidden block'>Rechercher une formation</span>
                            </button>
                        </div>
                    </div>

                    {/* Reset Button - Right aligned in row */}
                    <div className="flex items-center gap-3 justify-end absolute top-4 sm:top-6 right-6">
                        <button
                            onClick={handleReset}
                            className="flex items-center cursor-pointer gap-1 text-[#666] hover:text-[#1B365E] transition-colors text-[14px] whitespace-nowrap"
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            Réinitialiser
                        </button>
                    </div>

                    {/* Bottom Row - Checkboxes */}
                    <div className="flex flex-wrap gap-4 md:gap-8 pt-4 border-t border-[#EFEFEF]">
                        <Checkbox
                            checked={filters.presentiel}
                            onChange={(value) => emit({ presentiel: value })}
                            label="Formation en présentiel"
                            icon={
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                                    <polyline points="9 22 9 12 15 12 15 22" />
                                </svg>
                            }
                        />
                        <Checkbox
                            checked={filters.distance}
                            onChange={(value) => emit({ distance: value })}
                            label="Formation à distance"
                            icon={
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                                    <line x1="8" y1="21" x2="16" y2="21" />
                                    <line x1="12" y1="17" x2="12" y2="21" />
                                </svg>
                            }
                        />
                    </div>
                </div>
            </div>
        </div>
    )
}

interface CheckboxProps {
    checked: boolean
    onChange: (value: boolean) => void
    label: string
    icon: React.ReactNode
}

const Checkbox: React.FC<CheckboxProps> = ({ checked, onChange, label, icon }) => {
    return (
        <label
            className="flex items-center gap-2.5 cursor-pointer group"
            onClick={() => onChange(!checked)}
        >
            <div
                className={`w-5 h-5 rounded-[4px] border flex items-center justify-center transition-all duration-200 ${checked ? 'bg-[#1B365E] border-[#1B365E]' : 'bg-white border-[#5EB5C6] group-hover:border-[#1B365E]'}`}
            >
                {checked && (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                    </svg>
                )}
            </div>
            <span className="flex items-center gap-1.5 text-[#1B365E] text-[16px] font-medium">
                {icon}
                {label}
            </span>
        </label>
    )
}

export default SearchBar
